import { useEffect, useState } from "react";
import { api, formatCOP, formatNumber } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowRightLeft, Loader2, Search } from "lucide-react";

/** Mueve un lote completo a otro producto (ej. dos tonos fusionados por error en el importador). */
export default function TrasladarLoteDialog({ open, onClose, lote, productoId, productoNombre, onDone }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [dest, setDest] = useState(null);
  const [destLotes, setDestLotes] = useState(null);
  const [modo, setModo] = useState("sumar");
  const [motivo, setMotivo] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setQ(""); setRes([]); setDest(null); setDestLotes(null); setModo("sumar"); setMotivo(""); } }, [open]);

  useEffect(() => {
    if (!open || q.trim().length < 2) { setRes([]); return; }
    const t = setTimeout(async () => {
      setBuscando(true);
      try {
        const { data } = await api.get(`/productos?q=${encodeURIComponent(q.trim())}&limit=15`);
        setRes((Array.isArray(data) ? data : data.items || []).filter((p) => p.id !== productoId && !p.es_padre));
      } catch { setRes([]); } finally { setBuscando(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [q, open, productoId]);

  useEffect(() => {
    if (!dest) { setDestLotes(null); return; }
    api.get(`/productos/${dest.id}/lotes`).then((r) => setDestLotes(r.data)).catch(() => setDestLotes(null));
  }, [dest]);

  const [recordar, setRecordar] = useState(false);
  if (!lote) return null;
  const disp = Number(lote.cantidad_disponible || 0);
  const stockDest = Number(dest?.stock_actual || 0);
  const otrosLotes = (destLotes?.activos || []).filter((l) => l.contenedor_id && l.contenedor_id !== "INICIAL").reduce((s, l) => s + Number(l.cantidad_disponible || 0), 0);
  const inicialDisp = Math.max(0, stockDest - otrosLotes);
  const resultado = modo === "reemplazar_inicial" ? stockDest - inicialDisp + disp : stockDest + disp;

  const confirmar = async () => {
    if (!dest) { toast.error("Elige el producto destino"); return; }
    if (!motivo.trim()) { toast.error("Escribe el motivo"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/productos/lotes/${lote.id}/trasladar`, { producto_destino_id: dest.id, motivo: motivo.trim(), modo, recordar });
      if (recordar) toast.info("Listo: la próxima vez que llegue esta referencia irá directo al producto destino");
      toast.success(`Lote trasladado: ${formatNumber(data.cantidad)} und ahora en ${data.destino.nombre} (stock ${formatNumber(data.destino.stock_actual)})`);
      onDone?.(data); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo trasladar el lote"); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="trasladar-lote-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base"><ArrowRightLeft className="w-4 h-4 text-primary" /> Trasladar lote a otro producto</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="rounded-md border border-border bg-muted/30 p-2 text-xs">
            Lote <b>{lote.contenedor_id === "INICIAL" ? "inicial" : lote.contenedor_id}</b> de <b>{productoNombre}</b> · disponible <b>{formatNumber(disp)} und</b>{lote.costo_unitario !== undefined && <> · costo {formatCOP(lote.costo_unitario)}</>}
            <div className="text-muted-foreground mt-1">Lo ya vendido de este lote se queda en el producto actual (la utilidad histórica no cambia). Solo se mueve el saldo.</div>
          </div>
          <div>
            <Label className="text-xs">Producto destino</Label>
            {dest ? (
              <div className="flex items-center justify-between gap-2 rounded-md border border-primary/40 bg-primary/5 p-2 mt-1" data-testid="trasladar-destino-sel">
                <div className="min-w-0"><div className="font-medium truncate">{dest.nombre}</div><div className="text-[11px] text-muted-foreground">{dest.codigo || dest.sku} · stock {formatNumber(stockDest)}</div></div>
                <button type="button" className="text-xs text-primary underline" onClick={() => setDest(null)}>Cambiar</button>
              </div>
            ) : (
              <>
                <div className="relative mt-1">
                  <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nombre, código o SKU del producto que creaste" className="pl-7 h-9" data-testid="trasladar-buscar-input" />
                </div>
                {(res.length > 0 || buscando) && (
                  <div className="mt-1 max-h-44 overflow-auto rounded-md border border-border divide-y divide-border/60">
                    {buscando && <div className="p-2 text-xs text-muted-foreground flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Buscando…</div>}
                    {res.map((p, i) => (
                      <button type="button" key={p.id} onClick={() => setDest(p)} className="w-full text-left p-2 hover:bg-muted/50 text-xs" data-testid={`trasladar-opcion-${i}`}>
                        <div className="font-medium truncate">{p.nombre}</div>
                        <div className="text-muted-foreground">{p.codigo || p.sku || "sin código"} · stock {formatNumber(p.stock_actual || 0)}</div>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
          {dest && destLotes && (
            <div className="space-y-1.5">
              <Label className="text-xs">¿Qué hacer con el stock actual del destino?</Label>
              <label className="flex items-start gap-2 text-xs cursor-pointer"><input type="radio" checked={modo === "sumar"} onChange={() => setModo("sumar")} data-testid="trasladar-modo-sumar" /><span><b>Sumar</b>: el destino queda con {formatNumber(stockDest)} + {formatNumber(disp)} = <b>{formatNumber(stockDest + disp)}</b></span></label>
              <label className={`flex items-start gap-2 text-xs ${inicialDisp > 0 ? "cursor-pointer" : "opacity-50"}`}>
                <input type="radio" disabled={inicialDisp <= 0} checked={modo === "reemplazar_inicial"} onChange={() => setModo("reemplazar_inicial")} data-testid="trasladar-modo-reemplazar" />
                <span><b>Reemplazar el stock manual</b>: anula las {formatNumber(inicialDisp)} und que el destino tiene sin lote de contenedor (las cargaste a mano por este mismo contenedor) y deja <b>{formatNumber(stockDest - inicialDisp + disp)}</b>{inicialDisp <= 0 && " — el destino no tiene stock manual"}</span>
              </label>
            </div>
          )}
          <div>
            <Label className="text-xs">Motivo</Label>
            <Input value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Ej. eran dos naranjas distintos; el importador los fusionó" className="h-9 mt-1" data-testid="trasladar-motivo-input" />
          </div>
        </div>
                  <label className="flex items-start gap-2 cursor-pointer rounded-lg border border-border p-3" data-testid="trasladar-recordar-toggle">
            <input type="checkbox" checked={recordar} onChange={(e) => setRecordar(e.target.checked)} className="mt-0.5 accent-primary" data-testid="trasladar-recordar-check" />
            <div>
              <div className="text-sm font-semibold">Recordar para próximos contenedores</div>
              <div className="text-[11px] text-muted-foreground">Cuando vuelva a llegar la referencia <b>{lote.codigo || "de este lote"}</b> para <b>{productoNombre}</b>, el importador la mandará directo al producto destino. Se puede borrar en Configuración → Mapeos.</div>
            </div>
          </label>
<DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button onClick={confirmar} disabled={saving || !dest} data-testid="trasladar-confirmar-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : `Trasladar ${formatNumber(disp)} und`}{dest && !saving ? ` → stock ${formatNumber(resultado)}` : ""}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
